import { parseIsoDate } from './date';
import { err, ok, } from './types';
/**
 * Validates the input and builds the dependency graph.
 *
 * Reports, rather than throws, every error a user can produce by editing the
 * plan: duplicate tasks, relationships pointing at tasks that do not exist,
 * negative durations, and closed loops.
 */
export function buildGraph(tasks, dependencies) {
    const taskMap = new Map();
    for (const task of tasks) {
        if (taskMap.has(task.id)) {
            return err({ kind: 'duplicateTask', taskId: task.id });
        }
        if (!Number.isFinite(task.durationDays) || task.durationDays < 0) {
            return err({
                kind: 'invalidDuration',
                taskId: task.id,
                durationDays: task.durationDays,
            });
        }
        if (task.constraint !== undefined) {
            if (parseIsoDate(task.constraint.date) === undefined) {
                return err({
                    kind: 'invalidConstraintDate',
                    taskId: task.id,
                    date: task.constraint.date,
                });
            }
        }
        taskMap.set(task.id, task);
    }
    const successors = new Map();
    const predecessors = new Map();
    for (const id of taskMap.keys()) {
        successors.set(id, []);
        predecessors.set(id, []);
    }
    for (const dep of dependencies) {
        if (!taskMap.has(dep.predecessorId)) {
            return err({ kind: 'unknownTask', taskId: dep.predecessorId });
        }
        if (!taskMap.has(dep.successorId)) {
            return err({ kind: 'unknownTask', taskId: dep.successorId });
        }
        if (!Number.isInteger(dep.lagDays)) {
            // Half days do not exist in PST (decision A-11), so neither does a
            // fractional lag.
            return err({
                kind: 'invalidLag',
                predecessorId: dep.predecessorId,
                successorId: dep.successorId,
                lagDays: dep.lagDays,
            });
        }
        successors.get(dep.predecessorId)?.push(dep);
        predecessors.get(dep.successorId)?.push(dep);
    }
    const ordered = topologicalOrder(taskMap, successors, predecessors);
    if (!ordered.ok) {
        return err(ordered.error);
    }
    return ok({
        tasks: taskMap,
        successors,
        predecessors,
        order: ordered.value,
    });
}
/**
 * Kahn's algorithm. Returns the order, or the cycles that prevent one.
 *
 * Kept separate from buildGraph so the cycle search only runs when a cycle
 * actually exists - on a healthy plan this costs one pass over the graph.
 */
function topologicalOrder(tasks, successors, predecessors) {
    const remainingIncoming = new Map();
    for (const id of tasks.keys()) {
        remainingIncoming.set(id, predecessors.get(id)?.length ?? 0);
    }
    const ready = [];
    for (const [id, count] of remainingIncoming) {
        if (count === 0)
            ready.push(id);
    }
    const order = [];
    while (ready.length > 0) {
        const current = ready.pop();
        if (current === undefined)
            break;
        order.push(current);
        for (const dep of successors.get(current) ?? []) {
            const left = (remainingIncoming.get(dep.successorId) ?? 0) - 1;
            remainingIncoming.set(dep.successorId, left);
            if (left === 0)
                ready.push(dep.successorId);
        }
    }
    if (order.length === tasks.size) {
        return ok(order);
    }
    const stuck = new Set();
    for (const [id, count] of remainingIncoming) {
        if (count > 0)
            stuck.add(id);
    }
    return err({ kind: 'cycle', cycles: findCycles(stuck, successors) });
}
/**
 * Extracts concrete cycle paths from the tasks Kahn's algorithm could not
 * place, so the user interface can name the relationships to break instead of
 * only reporting that "something is circular".
 *
 * Depth-first search over the stuck subgraph; a back edge to a task on the
 * current stack closes a cycle. One cycle is reported per entry point, which
 * is enough to guide a user to the problem without flooding the message.
 */
function findCycles(stuck, successors) {
    const cycles = [];
    const visited = new Set();
    const stack = [];
    const onStack = new Set();
    const walk = (id) => {
        visited.add(id);
        stack.push(id);
        onStack.add(id);
        for (const dep of successors.get(id) ?? []) {
            const next = dep.successorId;
            if (!stuck.has(next))
                continue;
            if (onStack.has(next)) {
                const start = stack.indexOf(next);
                if (start >= 0) {
                    cycles.push({ path: [...stack.slice(start), next] });
                }
            }
            else if (!visited.has(next)) {
                walk(next);
            }
        }
        stack.pop();
        onStack.delete(id);
    };
    for (const id of stuck) {
        if (!visited.has(id))
            walk(id);
    }
    return cycles;
}
//# sourceMappingURL=graph.js.map